import { useQuery } from "@tanstack/react-query";
import { Activity, Filter } from "lucide-react";
import { useState } from "react";
import { api } from "../../lib/api";
import { formatTimestamp } from "../../lib/format";
import { SectionTitle } from "./shared";

type AuditCategory =
  | "auth"
  | "admin"
  | "trading"
  | "forecasting"
  | "reports"
  | "ingest"
  | "market_data"
  | "settings"
  | "system";

type AuditImportance = "info" | "notice" | "warning" | "critical";

type AuditEvent = {
  id: string;
  at: string;
  companyId: string | null;
  companyName: string | null;
  actorId: string | null;
  actorEmail: string | null;
  actorDisplayName: string | null;
  category: AuditCategory;
  importance: AuditImportance;
  action: string;
  subject: string | null;
  route: string | null;
  method: string | null;
  statusCode: number | null;
  ip: string | null;
  userAgent: string | null;
};

const AUDIT_CATEGORIES: AuditCategory[] = [
  "auth",
  "admin",
  "trading",
  "forecasting",
  "reports",
  "ingest",
  "market_data",
  "settings",
  "system",
];

const AUDIT_IMPORTANCES: AuditImportance[] = ["info", "notice", "warning", "critical"];

export function AuditLogSection({
  isGlobalAdmin,
  activeCompanyId,
  activeCompanyName,
}: {
  isGlobalAdmin: boolean;
  activeCompanyId: string | null;
  activeCompanyName: string | null;
}) {
  const [category, setCategory] = useState<AuditCategory | "">("");
  const [importance, setImportance] = useState<AuditImportance | "">("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [limit, setLimit] = useState(100);

  const auditQ = useQuery({
    queryKey: ["admin", "audit-log", activeCompanyId, category, importance, from, to, limit],
    queryFn: () => {
      const params = new URLSearchParams();
      params.set("limit", String(limit));
      if (category) params.set("category", category);
      if (importance) params.set("importance", importance);
      if (from) params.set("from", from);
      if (to) params.set("to", to);
      if (isGlobalAdmin && activeCompanyId) params.set("companyId", activeCompanyId);
      return api<{ events: AuditEvent[] }>(`/admin/audit-log?${params.toString()}`);
    },
  });

  return (
    <section className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <SectionTitle icon={<Activity size={18} />} title="Activity log" />
        {isGlobalAdmin ? (
          <div className="text-xs text-muted">
            {activeCompanyId ? activeCompanyName ?? "Active company" : "All companies"}
          </div>
        ) : null}
      </div>

      <div className="ops-toolbar flex flex-wrap items-center gap-2 text-xs">
        <Filter size={15} className="text-muted" />
        <select
          value={category}
          onChange={(e) => setCategory(e.target.value as AuditCategory | "")}
          className="bg-transparent border border-border rounded px-2 py-1.5"
        >
          <option value="">All categories</option>
          {AUDIT_CATEGORIES.map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </select>
        <select
          value={importance}
          onChange={(e) => setImportance(e.target.value as AuditImportance | "")}
          className="bg-transparent border border-border rounded px-2 py-1.5"
        >
          <option value="">All importance</option>
          {AUDIT_IMPORTANCES.map((i) => (
            <option key={i} value={i}>
              {i}
            </option>
          ))}
        </select>
        <input
          type="date"
          value={from}
          onChange={(e) => setFrom(e.target.value)}
          className="bg-transparent border border-border rounded px-2 py-1.5"
        />
        <input
          type="date"
          value={to}
          onChange={(e) => setTo(e.target.value)}
          className="bg-transparent border border-border rounded px-2 py-1.5"
        />
        <select
          value={limit}
          onChange={(e) => setLimit(Number(e.target.value))}
          className="bg-transparent border border-border rounded px-2 py-1.5"
        >
          {[50, 100, 200, 500].map((n) => (
            <option key={n} value={n}>
              {n} rows
            </option>
          ))}
        </select>
      </div>

      <div className="ops-table-frame border border-border rounded-md overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[1120px] text-sm">
            <thead className="text-xs text-muted border-b border-border">
              <tr>
                <th className="text-left p-2">Time</th>
                <th className="text-left p-2">Actor</th>
                <th className="text-left p-2">Category</th>
                <th className="text-left p-2">Importance</th>
                <th className="text-left p-2">Action</th>
                <th className="text-left p-2">Route</th>
                <th className="text-left p-2">Status</th>
                <th className="text-left p-2">Company</th>
                <th className="text-left p-2">IP</th>
              </tr>
            </thead>
            <tbody>
              {auditQ.isLoading ? (
                <tr>
                  <td colSpan={9} className="p-3 text-sm text-muted">
                    Loading...
                  </td>
                </tr>
              ) : (
                (auditQ.data?.events ?? []).map((event) => (
                  <AuditLogRow key={event.id} event={event} />
                ))
              )}
              {!auditQ.isLoading && (auditQ.data?.events ?? []).length === 0 ? (
                <tr>
                  <td colSpan={9} className="p-3 text-sm text-muted">
                    No activity.
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      </div>
      {auditQ.error instanceof Error ? (
        <div className="text-xs text-negRed">{auditQ.error.message}</div>
      ) : null}
    </section>
  );
}

function AuditLogRow({ event }: { event: AuditEvent }) {
  return (
    <tr className="border-b border-border/60 last:border-0 align-top">
      <td className="p-2 whitespace-nowrap text-xs">{formatTimestamp(event.at)}</td>
      <td className="p-2">
        <div className="max-w-56 truncate">{event.actorDisplayName || event.actorEmail || "Unknown"}</div>
        {event.actorDisplayName && event.actorEmail ? (
          <div className="text-xs text-muted max-w-56 truncate">{event.actorEmail}</div>
        ) : null}
      </td>
      <td className="p-2">
        <span className="rounded border border-border px-2 py-0.5 text-xs">{event.category}</span>
      </td>
      <td className="p-2">
        <span className={`rounded px-2 py-0.5 text-xs ${importanceClass(event.importance)}`}>
          {event.importance}
        </span>
      </td>
      <td className="p-2 max-w-64">
        <div className="truncate">{event.action}</div>
        {event.subject && event.subject !== event.route ? (
          <div className="text-xs text-muted truncate">{event.subject}</div>
        ) : null}
      </td>
      <td className="p-2">
        <div className="flex items-center gap-2">
          {event.method ? (
            <span className="rounded border border-border px-1.5 py-0.5 text-[10px] leading-none">
              {event.method}
            </span>
          ) : null}
          <span className="text-xs text-muted max-w-56 truncate">{event.route ?? "—"}</span>
        </div>
      </td>
      <td className="p-2 text-xs">{event.statusCode ?? "—"}</td>
      <td className="p-2 text-xs max-w-44 truncate">{event.companyName ?? "Platform"}</td>
      <td className="p-2 text-xs max-w-36 truncate">{event.ip ?? "—"}</td>
    </tr>
  );
}

function importanceClass(importance: AuditImportance): string {
  switch (importance) {
    case "critical":
      return "bg-negRed/15 text-negRed";
    case "warning":
      return "bg-amber-500/15 text-amber-600 dark:text-amber-300";
    case "notice":
      return "bg-accent/15 text-accent";
    default:
      return "bg-muted/10 text-muted";
  }
}
